import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, PackagePlus, AlertTriangle } from 'lucide-react';

const LABEL = 'text-white/40 text-[10px] font-bold tracking-widest mb-1 block';

// Books a delivery batch in. The Square on-hand figure is incremented, the
// product's running received-stock total moves up and the batch is written to
// the Stock Adjustment audit trail under the 'Stock Received' reason.
export default function StockReceiveModal({ row, onClose, onReceived }) {
  const [qty, setQty] = useState('');
  const [supplier, setSupplier] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [receipt, setReceipt] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!row) return null;
  const { product, onHand } = row;

  const received = Number(qty);
  const cost = Number(unitCost);
  const valid =
    qty !== '' && Number.isFinite(received) && received > 0 &&
    supplier.trim() !== '' &&
    unitCost !== '' && Number.isFinite(cost) && cost >= 0;

  const projected = valid ? onHand + received : null;
  const lineTotal = valid ? received * cost : 0;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('stockControl', {
        action: 'receive',
        product_id: product.id,
        quantity: received,
        supplier: supplier.trim(),
        unit_cost_ex_vat: cost,
        receipt_reference: receipt.trim(),
        note,
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Stock receipt failed');
      onReceived(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Stock receipt failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white text-base font-black uppercase tracking-wider">
            Receive Stock
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            {product.name}
            {product.variation_name ? ` — ${product.variation_name}` : ''}
            {product.sku ? ` · ${product.sku}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-white/10 bg-[#0d0d0f] px-4 py-3">
              <p className="text-white/40 text-[10px] font-bold tracking-widest">CURRENT SQUARE LEVEL</p>
              <p className="text-white font-black text-lg">{onHand}</p>
            </div>
            <div className="border border-white/10 bg-[#0d0d0f] px-4 py-3">
              <p className="text-white/40 text-[10px] font-bold tracking-widest">ALREADY RECEIVED</p>
              <p className="text-white font-black text-lg">{Number(product.stock_purchased) || 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>QUANTITY RECEIVED *</label>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="seo-input"
                autoFocus
              />
            </div>
            <div>
              <label className={LABEL}>UNIT COST EX VAT (£) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                className="seo-input"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={LABEL}>SUPPLIER *</label>
              <input
                type="text"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                className="seo-input"
                placeholder="e.g. Booker"
              />
            </div>
            <div>
              <label className={LABEL}>RECEIPT REFERENCE</label>
              <input
                type="text"
                value={receipt}
                onChange={(e) => setReceipt(e.target.value)}
                className="seo-input"
                placeholder="e.g. INV-88421"
              />
            </div>
          </div>

          {projected != null && (
            <div className="flex items-center gap-3 border border-white/10 bg-[#0d0d0f] px-4 py-3">
              <span className="text-white/40 text-[10px] font-bold tracking-widest">NEW SQUARE LEVEL</span>
              <span className="text-white font-black">{onHand}</span>
              <span className="text-white/30">→</span>
              <span className="text-green-400 font-black">{projected}</span>
              <span className="ml-auto text-[10px] font-bold tracking-widest text-white/40">
                BATCH £{lineTotal.toFixed(2)}
              </span>
            </div>
          )}

          <div>
            <label className={LABEL}>NOTE (OPTIONAL)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="seo-input resize-y"
              placeholder="Anything worth recording against this delivery"
            />
          </div>

          <p className="text-white/30 text-[11px] leading-relaxed">
            The unit cost becomes this item's cost price, so the margin columns and Square's COGS stay current.
          </p>

          {error && (
            <p className="flex items-start gap-2 text-red-400 text-[11px] border border-red-400/30 bg-red-400/5 px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </p>
          )}

          <div className="flex gap-3">
            <button
              onClick={submit}
              disabled={!valid || saving}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />}
              BOOK IN TO SQUARE
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="border border-white/10 hover:border-white/25 text-white/50 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}